import { useState } from 'react';
import SubTabs from '../components/SubTabs.jsx';
import ChannelGroupsSection from '../components/registry/ChannelGroupsSection.jsx';
import BrandMappingSection from '../components/registry/BrandMappingSection.jsx';
import ValueGroupsSection from '../components/registry/ValueGroupsSection.jsx';

// One tab per dimension the calibration engine can group by, each reconciling the two
// sources' vocabularies for that dimension. These four are the whole surface.
//
// Brand Mapping also carries ownership — which producer and brands are the client's own.
// That used to be its own "Our Brands" tab, and splitting them was wrong: you only ever
// have actual spend for your own brands, so deciding ownership on a different screen from
// the mapping is how the two end up disagreeing.
//
// Two tabs were removed once the four dimension mappings covered the ground:
//
// "Match Keys" was a raw editor over cm_match_key_map — the same table Channel Mapping
// writes. It offered nothing Channel Mapping does not, covered only channels, and let a key
// be edited out from under a group. repo_registry.upsert_match_key survives as the internal
// call the channel-group save path makes.
//
// "Data Quality" claimed to show which (month, channel, brand) buckets had both sources, and
// was wrong twice over. It compared raw values, so every mapping on this page was invisible
// to it and a mapped pair still counted as two one-sided buckets; and it read cm_actual_spend,
// which only the manual-entry screen writes, so every imported file was missing from the
// "actual" side. It reported gaps that were not gaps.
const KIND_TABS = [
  { id: 'channel',    label: 'Channel Mapping' },
  { id: 'brandmap',   label: 'Brand Mapping' },
  { id: 'market',     label: 'Market Mapping' },
  { id: 'format',     label: 'Creative Format' },
];

// Creative format is Statex's `visual_type` column. The two names are used
// interchangeably in the data; the UI settles on "creative format" because that is what
// the actual-spend sources call it and what the calibration level is named after.
const MARKET_HELP = `Statex and the actual-spend sources name markets differently, and the `
  + `grouping is genuinely many-to-many: an actuals line for a region can cover several `
  + `Statex markets, and one market can arrive under several spellings. Group everything `
  + `that describes the same market together.`;

const FORMAT_HELP = `Creative format is Statex's visual type. Statex mixes the publisher `
  + `into it — "Facebook - Banner" is publisher Facebook, format Banner — so values are `
  + `split on " - " where nothing is mapped. Group the spellings that mean the same format; `
  + `it is many-to-many, since each source names them its own way.`;

export default function DataMappingPage() {
  const [kind, setKind] = useState('channel');

  return (
    <div style={{ padding: 20 }}>
      <h4>Data Mapping</h4>
      <p className="muted" style={{ marginBottom: 16 }}>
        Reconcile the vocabularies of the two data sources so calibration can match them:
        Statex competitive on one side, the actual-spend imports on the other. Mappings are
        applied at read time — historical rows keep their raw values and re-read through the
        current mapping, so a correction restates past figures rather than only future ones.
      </p>

      <SubTabs tabs={KIND_TABS} active={kind} onChange={setKind} />

      {kind === 'channel' ? <ChannelGroupsSection />
        : kind === 'brandmap' ? <BrandMappingSection />
        : kind === 'market' ? (
            <ValueGroupsSection kind="market" noun="markets" help={MARKET_HELP} />
          )
        : <ValueGroupsSection kind="creative_format" noun="creative formats" help={FORMAT_HELP} />}
    </div>
  );
}
